import { _ } from "../../../core/polyglot/polyglot.js";
import { convertHeaderCellsToTableRowsGroup } from "../visitors/ColumnTitleRenderer/HeaderCell.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ColumnTitleRenderer from "../visitors/ColumnTitleRenderer/ColumnTitleRenderer.js";
import CountWidget from "../../../core/widgets/CountWidget/CountWidget.js";
import CssClassNameVisitor from "../../../models/reports/reports/CssClassNameVisitor.js";
import RowRenderer from "../RowRenderer/RowRenderer.js";

/**
 * Render an instance of {@link Report} as a <table> DOM element. Each
 * row of the report is represented as a <tr> or as multiple <tr>
 * elements in the case of a {@link GroupRow}.
 *
 * @param {Report} report - The report to render
 * @param {Renderer} [itemTitleRenderer] - Takes an {@link
 *   AbstractRow} as argument and should return a short representation
 *   of this item. The default is to use the return value of calling
 *   `getItem()` on the argument
 * @param {Renderer} [headRenderer] - Takes a `columnsTitleRenderer`,
 *   a `report` and a `columns` arguments and is expected to return a
 *   renderable of the content of <thead>. The function may use the
 *   `columnsTitleRenderer` argument to render a <th> for each of the
 *   `columns`. The default value renders a <tr> with the report's
 *   name in the first <th> and the column titles in the followind
 *   table cells
 * @param {Renderer} [budgetNameRenderer] - Takes a budget name as
 *   argument and should return a renderable for it
 * @param {Renderer} [itemDimensionsRenderer] - Takes an {@link AbstractItem}
 *  name as argument and should return a renderable for it
 *  @param {Renderer} [estimateNameRenderer] - Takes a ForecastEstimate id as
 *   argument and should return a renderable for it. By default, it returns
 *   the id.
 * @param {function} [makeRowRenderer] - Should return a function
 *   taking a row as argument and returning <tr> elements (usually one
 * @param {function} [makeRowTitleRenderer] - Should return a function
 *   taking a row as argument and returning <th> element. If absent,
 *   the function returned by `makeRowRenderer` is expected to take
 *   care of rendering row titles
 * @param {function(AbstractColumn): string} [getColumnCssClasses] - This
 *   function is expected to return CSS classes for the column passed
 *   as argument. This is used inside the <th> of the column's title
 * @param {Renderer} [dataCellRenderer] - This function is called with
 *  a row, a column, a CSS class and a content and is expected to
 *  render a <td> with the specified content
 *   but more for {@link GroupRow}) Take Use {@link RowRenderer} by
 *   default
 * @param {bool} isFlat - Should be false if the receiver should display
 *   an expand/collapse arrow next to rows with child rows. If this is `true`,
 *   `onGroupRowCollapseExpandRequest` will never be called
 * @param {bool} [showReportTitle] - True if the first cell should
 *   render the report title
 */
export default class ReportTableWidget extends Widget {
	constructor({
		report,
		itemTitleRenderer,
		headRenderer,
		budgetNameRenderer,
		itemDimensionsRenderer,
		estimateNameRenderer,
		makeRowRenderer,
		makeRowTitleRenderer,
		getColumnCssClasses,
		dataCellRenderer,
		isFlat,
		showReportTitle = true,
	}) {
		super(...arguments);

		this._report = report;
		this._showReportTitle = showReportTitle;

		this._itemTitleRenderer =
			itemTitleRenderer || this._defaultItemTitleRenderer.bind(this);

		this._headRenderer =
			headRenderer || this._defaultHeadRenderer.bind(this);

		const dimensionsRenderer =
			itemDimensionsRenderer ||
			this._defaultItemDimensionsRenderer.bind(this);

		const rowRendererMaker =
			makeRowRenderer || this._makeDefaultRowRenderer.bind(this);

		this._rowRenderer = rowRendererMaker({
			report,
			dataCellRenderer:
				dataCellRenderer || this._defaultDataCellRenderer.bind(this),
			itemTitleRenderer: this._itemTitleRenderer,
			rowTitleRenderer: makeRowTitleRenderer?.({
				report,
				itemTitleRenderer: this._itemTitleRenderer,
				dimensionsRenderer,
				onGroupRowCollapseExpandRequest:
					this._collapseExpand.bind(this),
			}),
			dimensionsRenderer,
			columns: report.getLeafVisibleColumns(),
			isFlat,
		});

		this._columnsTitleRenderers = this._makeColumnTitleRenderers({
			itemTitleRenderer: this._itemTitleRenderer,
			budgetNameRenderer:
				budgetNameRenderer ||
				this._defaultBudgetNameRenderer.bind(this),
			dimensionsRenderer,
			estimateNameRenderer:
				estimateNameRenderer ||
				this._defaultEstimateNameRenderer.bind(this),
			getColumnCssClasses:
				getColumnCssClasses ||
				this._defautCssClassNameRenderer.bind(this),
		});
	}

	renderContentOn(html) {
		this._report.withCoALanguage(() => {
			html.table(
				html.thead(this._renderHeadOn.bind(this)),
				html.tbody(this._renderBodyOn.bind(this)),
			);
		});
	}

	_renderHeadOn(html) {
		const report = this._report;
		const columns = this._report.getVisibleColumns();
		const headerCellsArray = this._columnsTitleRenderers(columns);
		const columnsTitleRenderer = ({ title }) =>
			convertHeaderCellsToTableRowsGroup({
				title,
				headerCellsArray,
			});

		html.render(
			this._headRenderer({
				report,
				columns,
				columnsTitleRenderer,
			}),
		);
	}

	_renderBodyOn(html) {
		html.render(this._report.getVisibleRows().map(this._rowRenderer));
	}

	_defaultItemTitleRenderer(item) {
		return item.getTitle();
	}

	_defaultItemDimensionsRenderer(item) {
		if (!item.hasOwnDimensionSpecification()) {
			return (_html) => {};
		}

		return (html) => {
			const dimensionLabels = item.getDimensionLabels();

			html.span({ klass: "dimensions" }, (html) => {
				if (dimensionLabels.length === 1) {
					html.render(dimensionLabels[0]);
				} else {
					html.render(
						new CountWidget({
							elements: dimensionLabels,
							pluralLabel: _("dimensions"),
						}),
					);
				}
			});
		};
	}

	_defaultDataCellRenderer({ content = "", cssClass = "" }) {
		return (html) => html.td({ class: cssClass }, content);
	}

	_makeDefaultRowRenderer({
		report,
		columns,
		dataCellRenderer,
		itemTitleRenderer,
		dimensionsRenderer,
		rowTitleRenderer,
		isFlat,
	}) {
		const visitor = new RowRenderer({
			report,
			columns,
			dataCellRenderer,
			itemTitleRenderer,
			dimensionsRenderer,
			rowTitleRenderer,
			isFlat,
			onGroupRowCollapseExpandRequest: this._collapseExpand.bind(this),
		});

		return (row) => visitor.visit(row);
	}

	_defautCssClassNameRenderer() {
		return new CssClassNameVisitor();
	}

	_defaultHeadRenderer({ columnsTitleRenderer }) {
		return (html) =>
			html.render(
				columnsTitleRenderer({
					title: (html) =>
						html.th(
							{ class: "report-title" },
							this._showReportTitle
								? this._report.getTitle().toUpperCase()
								: "",
						),
				}),
			);
	}

	_defaultBudgetNameRenderer(budgetName) {
		return budgetName;
	}

	_defaultEstimateNameRenderer({ forecastEstimateId }) {
		return forecastEstimateId;
	}

	_collapseExpand(groupRow) {
		groupRow.toggleCollapsed();
		this.update();
	}

	_makeColumnTitleRenderers({
		itemTitleRenderer,
		budgetNameRenderer,
		dimensionsRenderer,
		estimateNameRenderer,
		getColumnCssClasses,
	}) {
		const visitor = new ColumnTitleRenderer({
			itemTitleRenderer,
			budgetNameRenderer,
			dimensionsRenderer,
			estimateNameRenderer,
			getColumnCssClasses,
		});

		return (columns) => visitor.visitAll(columns);
	}
}
